import { View, Text } from "@src/components";
import { CoverImage } from "@src/components/CoverImage";
import { ScalePressable } from "@src/components/ScalePressable";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { Clip } from "../types/clip";
import { formatTime } from "../utils";
import { memo } from "react";

interface ClipTimelineItemProps {
  clip: Clip;
  isSelected: boolean;
  onPress: () => void;
  width: number;
  height: number;
  timezone: string;
}

export const ClipTimelineItem = memo(
  ({ clip, isSelected, onPress, width, height, timezone }: ClipTimelineItemProps) => {
    const { theme } = useUnistyles();

    const borderWidth = isSelected ? 3 : 0;

    return (
      <ScalePressable onPress={onPress} style={styles.pressable}>
        <View
          style={[
            styles.container,
            {
              width,
              height,
              borderWidth,
              borderColor: isSelected ? theme.colors.teal[400] : "transparent",
            },
          ]}
        >
          {clip.thumbnailUrl ?
            <CoverImage uri={clip.thumbnailUrl} />
          : <View style={styles.noThumbnailContainer}>
              <Text textStyle="xs/regular" color="textMuted">
                No thumbnail
              </Text>
            </View>
          }

          <View style={styles.timestampContainer}>
            <View style={styles.timestamp}>
              <Text textStyle="sm/semibold" style={styles.timestampText}>
                {formatTime(clip.timestamp, timezone)}
              </Text>
            </View>
          </View>
        </View>
      </ScalePressable>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  pressable: {
    marginHorizontal: theme.spacing.sm,
  },
  container: {
    borderRadius: theme.spacing.md,
    overflow: "hidden",
    position: "relative",
  },
  noThumbnailContainer: {
    width: "100%",
    height: "100%",
    backgroundColor: theme.colors.muted as string,
    alignItems: "center",
    justifyContent: "center",
  },
  timestampContainer: {
    position: "absolute",
    bottom: theme.spacing.sm,
    left: 0,
    right: 0,
    alignItems: "center",
  },
  timestamp: {
    backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.spacing.md,
  },
  timestampText: {
    color: theme.colors.mediaOverlay.foreground as string,
  },
}));
